import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Switch, Route, useHistory, useParams, useLocation } from "react-router-dom";

// MP Component Library
import { Alert, Button, Input, Loader, ResponsiveLogo } from "@mindprint-learning/mindprint-components";

// Api Calls
import { addTrialEmail, getCNBData, getTestUrlForToken, validateTrial } from "../../api";

// Components
import AttentionScreener from "../reports/templates/AttentionScreener";

const Trial = () => {
  const [step, setStep] = useState(0);
  const [isResultsPage, setIsResultsPage] = useState(false);
  const location = useLocation();
  useEffect(() => {
    setIsResultsPage(location.pathname.search("results") !== -1);
  }, [location]);

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          backgroundColor: "#2c3436",
          paddingLeft: 60,
          paddingBottom: 20,
          paddingTop: 20,
          paddingRight: 60,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          zIndex: 5000,
          boxShadow: "rgb(0 0 0 / 20%) 0px 0px 24px 0px",
          height: 100,
        }}
      >
        <a href="https://www.mindprintlearning.com" target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}>
          <ResponsiveLogo dark={false} width={200} />
        </a>
        <div style={{ fontSize: 12, color: "#fff", textAlign: "right" }}>
          <form action="https://mindprintlearning.com" target="_blank" rel="noreferrer">
            <Button background="none" borderColor="#f37576" color="#f37576">
              About MindPrint
            </Button>
          </form>
        </div>
      </div>

      <div style={{ paddingTop: 130, width: "100%", minHeight: "calc(100vh - 100px)", maxWidth: 800 }}>
        {!isResultsPage && (
          <div style={{ marginBottom: 20, paddingLeft: 60, paddingRight: 60, textAlign: "center" }}>
            <h1 style={{ marginBottom: 20, fontSize: 42, color: "#2c3436" }}>MindPrint Attention Screener</h1>
            <div style={{ fontSize: 14, fontStyle: "italic", marginBottom: 20 }}>Time to complete: Approximately 10 minutes</div>
            <div style={{ marginBottom: 20, fontSize: 18 }}>
              The MindPrint Attention Screener is one task that identifies <br />
              your ability to sustain focus.
            </div>
          </div>
        )}

        <StepperContainer stepCount={4} stepIndex={step} backgroundColor={isResultsPage ? "#f5f6fa" : "#2c3436"} color={isResultsPage ? "#2c3436" : "#fff"}>
          <Switch>
            <Route exact path="/error">
              <Error setStep={setStep} />
            </Route>
            <Route exact path="/">
              <EnterPassword setStep={setStep} />
            </Route>
            <Route path="/email/:token">
              <EnterEmail setStep={setStep} />
            </Route>
            <Route path="/start/:token">
              <Test setStep={setStep} />
            </Route>
            <Route exact path="/results/:token*">
              <Result setStep={setStep} />
            </Route>
          </Switch>
        </StepperContainer>
      </div>
      <Footer />
    </div>
  );
};

export default Trial;

const StepperContainer = ({ backgroundColor, color, children, stepCount, stepIndex }) => {
  const variants = { initial: { opacity: 0.2 }, active: { opacity: 1.0 } };
  return (
    <motion.div style={{ width: "100%", paddingLeft: 60, paddingRight: 60 }}>
      <motion.div style={{ backgroundColor: backgroundColor, borderRadius: 12, overflow: "hidden", color: color }}>{children}</motion.div>
      <motion.div style={{ width: "100%", height: 60, display: "flex", marginBottom: 20, marginTop: 20 }}>
        {new Array(stepCount).fill(0).map((step, i) => {
          return (
            <motion.div
              animate={stepIndex < i ? "initial" : "active"}
              variants={variants}
              key={"stepper-" + i}
              style={{ flex: 1, backgroundColor: "#00b7bf", margin: 20, borderRadius: 12 }}
            />
          );
        })}
      </motion.div>
    </motion.div>
  );
};

const EnterPassword = ({ setStep }) => {
  const [password, setPassword] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const history = useHistory();

  useEffect(() => {
    setStep(0);
  }, []);

  const submitPassword = () => {
    validateTrial(password).then((value) => {
      if (value.result === "success") {
        history.push(`/email/${value.token}`);
      } else {
        setShowAlert(true);
      }
    });
  };
  return (
    <div style={{ display: "flex", justifyContent: "center", flexDirection: "column", padding: 30, textAlign: "center" }}>
      <h3 style={{ marginBottom: 10, fontSize: 28 }}>Sign In</h3>
      <div>Please enter the passcode to get started</div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", marginTop: 20, marginBottom: 20 }}>
        <div>
          <Input
            style={{ width: 300, textAlign: "center", fontSize: 26 }}
            color="#00b7bf"
            type="password"
            onChange={(value) => {
              setPassword(value);
            }}
          />
        </div>
      </div>
      <div>
        <Button onClick={() => submitPassword()} background="#f37576">
          submit
        </Button>
      </div>
      <Alert show={showAlert} title="Oops!" description="The password you entered is incorrect. Please try again." close={() => setShowAlert(false)} />
    </div>
  );
};

const EnterEmail = ({ setStep }) => {
  const params = useParams();
  const history = useHistory();

  const [email, setEmail] = useState("");
  const [emailValid, setEmailValid] = useState(false);

  const [showAlert, setShowAlert] = useState(false);

  const url = "https://trial-v2.mindprintlearning.com/start/" + params.token;
  useEffect(() => {
    setStep(1);
  }, []);

  const submitEmail = () => {
    addTrialEmail(email, params.token).then((value) => {
      if (value.result === "success") {
        history.push(`/start/${params.token}`);
      } else {
        alert("oops....");
      }
    });
  };
  return (
    <div style={{ display: "flex", justifyContent: "center", flexDirection: "column", padding: 30, textAlign: "center" }}>
      <h3 style={{ marginBottom: 10, fontSize: 28 }}>Enter Your Email*</h3>
      <div>Your confidential results will be sent to the email address you provide</div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", marginTop: 20, marginBottom: 20 }}>
        <div>
          <Input
            type="email"
            style={{ width: 500, fontSize: 26 }}
            color="#00b7bf"
            onChange={(value) => setEmail(value)}
            validCallback={(value) => setEmailValid(value)}
          />
        </div>
      </div>
      <div>
        <Button onClick={() => submitEmail()} disabled={!emailValid} background="#f37576">
          submit
        </Button>
      </div>
      <div style={{ marginTop: 40, fontSize: 14, fontStyle: "italic", marginBottom: 20 }}>
        *If you would prefer not to receive an email, please copy and save the link below. If you lose this link, you will not be able to see your results.
      </div>
      <a href={url} style={{ fontWeight: 600, color: "#00b7bf" }}>
        {url}
      </a>
      <Alert show={showAlert} title="Oops!" description="You entered an invalid email. Please try again." close={() => setShowAlert(false)} />
    </div>
  );
};

const Test = ({ setStep }) => {
  const params = useParams();
  const history = useHistory();

  const [url, setUrl] = useState();

  useEffect(() => {
    setStep(2);
  }, []);

  useEffect(() => {
    // check for completed_at date. if exists, forward to results page
    getTestUrlForToken(params.token).then((value) => {
      console.log(value);

      if (value.result === "success") {
        if (value.urlType === "results") {
          history.push(value.url);
        }
        setUrl(value.url);
      } else {
        history.push("/error");
      }
    });
  }, []);
  return (
    <div style={{ textAlign: "center", padding: 60 }}>
      <div style={{ marginBottom: 20, fontStyle: "italic", fontSize: 24 }}>Do not use a touchscreen</div>
      <div style={{ marginBottom: 20, fontStyle: "italic" }}>
        Remove any distractions before you begin. Try to work as accurately and as quickly as possible throughout this brief task.
      </div>

      {url && (
        <div style={{ marginTop: 20 }}>
          <a style={{ color: "#f37576", fontWeight: 900, fontSize: 24 }} href={url}>
            Take the Attention Screener
          </a>
        </div>
      )}
    </div>
  );
};

const Error = ({ setStep }) => {
  useEffect(() => {
    setStep(-1);
  }, []);

  return (
    <div style={{ textAlign: "center", padding: 60 }}>
      <h1 style={{ marginBottom: 20 }}>We're sorry, but we can't find your results</h1>
      <div>
        <div style={{ fontStyle: "italic", marginBottom: 20 }}>Please contact us for assistance:</div>
        <a style={{ color: "#f37576", fontWeight: 900, fontSize: 18 }} href="mailto:support@mindprintlearning.com">
          support@mindprintlearning.com
        </a>
      </div>
    </div>
  );
};

const Result = ({ setStep }) => {
  const location = useLocation();
  const [data, setData] = useState();
  const [loaded, setLoaded] = useState(false);
  const history = useHistory();
  //
  const [showRawScores, _setShowRawScores] = useState(false);
  const showRawScoresRef = useRef(showRawScores);
  const setShowRawScores = (data) => {
    showRawScoresRef.current = data;
    _setShowRawScores(data);
  };

  /* Check raw scores  */
  const handleUserKeyPress = (event) => {
    if (event.shiftKey && event.ctrlKey && event.key === "T") {
      setShowRawScores(!showRawScoresRef.current);
    }
  };
  useEffect(() => {
    window.addEventListener("keydown", handleUserKeyPress);

    return () => {
      window.removeEventListener("keydown", handleUserKeyPress);
    };
  }, []);

  useEffect(() => {
    setStep(3);
  }, []);

  useEffect(() => {
    const pid = location.search.split("?id=")[1];
    if (pid) {
      getCNBData(pid).then((value) => {
        if (value.result === "success") {
          setData(value);
          setLoaded(true);
        } else {
          history.push("/error");
        }
      });
    } else {
      history.push("/error");
    }
  }, []);

  return (
    <div>
      <AnimatePresence initial={{ opacity: 0 }} animate={{ opacity: 1.0, transition: { delay: 2 } }} exit={{ opacity: 0 }}>
        {loaded && <AttentionScreener key={"att-screener-1"} data={data} color={"#f5f6fa"} />}
      </AnimatePresence>
      {!loaded && <Loader show={!loaded} />}
      {showRawScores && <DevMenu data={data} />}
    </div>
  );
};

const DevMenu = ({ data }) => {
  const categories = ["low weakness", "medium weakness", "low expected", "medium expected", "high expected", "medium strength", "high strength"];
  return (
    <div style={{ position: "fixed", top: 98, left: 0, bottom: 0, width: 260, backgroundColor: "#eb4d4b", padding: 30, color: "#fff", fontSize: 14 }}>
      <h2 style={{ marginBottom: 20 }}>:: dev menu ::</h2>
      <div style={{ fontStyle: "italic", fontSize: 12 }}>v-2.0.4</div>
      <h3>attention</h3>
      <h4>Overall Category:</h4>
      <div>Category: {data.attention.reportValues.index} (out of 5)</div>
      <div>Bars: {data.attention.reportValues.bars}</div>
      <h4>score</h4>
      <div>{data.attention.score.raw}</div>
      <div> {categories[data.attention.score.category]}</div>
      <h4>accuracy</h4>
      <div>{data.attention.accuracy.score}</div>
      <div>Category: {data.attention.accuracy.category}</div>
      <h4>speed</h4>
      <div>{data.attention.speed.score}</div>
      <div>Category: {data.attention.speed.category}</div>
      <h4>efficiency</h4>
      <div>{data.attention.efficiency}</div>
    </div>
  );
};

const Footer = () => {
  return (
    <div
      style={{
        bottom: 0,
        //  backgroundColor: "rgba(44, 52, 54, 0.05)",
        height: 100,
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <FooterItem label="Mindprint" url="https://mindprintlearning.com/" />
      <Sep />
      <FooterItem label="Schools" url="https://www.mindprintlearning.com/outcomes" />
      <Sep />
      <FooterItem label="Tutors" url="https://www.mindprintlearning.com/tutoring" />
      <Sep />
      <FooterItem label="Parents" url="https://www.mindprintlearning.com/parents" />
      <Sep />
      <FooterItem label="Blog" url="https://www.mindprintlearning.com/about#blog" />
    </div>
  );
};

const FooterItem = ({ label, url }) => {
  return (
    <div style={{ margin: 20 }}>
      <a href={url} target="_blank" rel="noreferrer" style={{ color: "#00b7bf", textDecoration: "none", fontSize: 14 }}>
        {label}
      </a>
    </div>
  );
};

const Sep = () => {
  return <div style={{ width: 1, height: 20, backgroundColor: "#00b7bf" }} />;
};

/*
0: low weakness
1: medium weakness
2: low expected
3: medium expected
4: high expected
5: medium strength
6: high strength

*/

/*

OVERVIEW:
1) Enter your password
2) If valid -> Enter your email (*we will never spam your account - opt in? "i'd like to receive updates"*)
// Now at test/token...
3a) Send the trial-v2.minprintlearning.com link to email
3b) Message: "Click on the *secure link* below - We've also sent this link to your email. Use this link to complete the test AND view your results when you are finished"
    - CNB link is generated from api based on subid in url
4) Click link -> CNB in new tab
5) CNB Finish -> Redirect to /results page


DUE FRIDAY the 9th
- Sub out face memorory? (for fun) - If it's too creepy, stick with attention...
- Send link (or screenshots) to Nancy
- 

*/
